/**
 * Auctioneer Live Console
 * Primary interface for conducting live auctions
 */
import { useState, useEffect, useCallback } from 'react';
import { axiosInstance } from '../../config/axios';
import { toast } from 'sonner';
import { formatBYC, formatBYCCompact, AUCTION_STATUS_COLORS, getErrorMessage, useCurrentUser } from './useAuction';
import CareerStatsTabs from './CareerStatsTabs';

// Photo Overlay Component
function PhotoOverlay({ photoUrl, playerName, onClose }) {
  if (!photoUrl) return null;
  
  return (
    <div 
      className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div className="relative max-w-2xl max-h-[80vh]">
        <button
          onClick={onClose}
          className="absolute -top-10 right-0 text-white hover:text-gray-300 transition-colors"
        >
          <i className="ph-bold ph-x text-2xl"></i>
        </button>
        <img 
          src={photoUrl} 
          alt={playerName} 
          className="max-w-full max-h-[80vh] rounded-xl shadow-2xl object-contain"
          onClick={(e) => e.stopPropagation()}
        />
        <p className="text-white text-center mt-3 font-bold">{playerName}</p>
      </div>
    </div>
  );
}

export default function LiveAuctionConsole({ eventId, config, phase, onStateChange }) {
  const [currentPlayer, setCurrentPlayer] = useState(null);
  const [teams, setTeams] = useState([]);
  const [recentBids, setRecentBids] = useState([]);
  const [liveStats, setLiveStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPhotoOverlay, setShowPhotoOverlay] = useState(false);
  
  // Bid form state
  const [selectedTeam, setSelectedTeam] = useState('');
  const [bidAmount, setBidAmount] = useState('');
  const [placing, setPlacing] = useState(false);
  
  const isLive = config?.auction_status === 'LIVE';
  const isPaused = config?.is_paused;
  const { isAdmin } = useCurrentUser();

  // Phase start time gate for Start Auction button
  const auctionPhaseStarted = !phase?.start_date || new Date() >= new Date(phase.start_date);
  const phaseStartLabel = phase?.start_date
    ? new Date(phase.start_date).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })
    : null;

  // Revert player status (Admin only)
  const handleRevertStatus = async () => {
    if (!currentPlayer) return;
    if (!window.confirm(`Revert ${currentPlayer.player_name} back to AVAILABLE? This will undo the ${currentPlayer.status} action.`)) return;
    
    try {
      await axiosInstance.post(`/v1/events/${eventId}/auction/players/${currentPlayer.id}/revert`, { confirm: true });
      toast.success('Player status reverted to AVAILABLE');
      fetchData();
      onStateChange?.();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to revert status'));
    }
  };

  const fetchData = useCallback(async () => {
    try {
      const [teamsRes, statsRes] = await Promise.all([
        axiosInstance.get(`/v1/events/${eventId}/auction/teams`),
        axiosInstance.get(`/v1/events/${eventId}/auction/live-stats`)
      ]);
      setTeams(teamsRes.data);
      setLiveStats(statsRes.data);
      
      // Get current player
      try {
        const playerRes = await axiosInstance.get(`/v1/events/${eventId}/auction/current-player`);
        const playerData = playerRes.data;
        // Extract the player object and merge with bid info
        if (playerData.has_current_player && playerData.player) {
          setCurrentPlayer({
            ...playerData.player,
            current_bid: playerData.current_bid,
            bid_count: playerData.bid_count,
          });
          // Fetch recent bids for current player
          const bidsRes = await axiosInstance.get(`/v1/events/${eventId}/auction/bids?player_pool_id=${playerData.player.id}&page_size=10`);
          // API returns array directly, not { bids: [...] }
          const bidsData = Array.isArray(bidsRes.data) ? bidsRes.data : (bidsRes.data.bids || []);
          setRecentBids(bidsData);
        } else {
          setCurrentPlayer(null);
          setRecentBids([]);
        }
      } catch (err) {
        if (err.response?.status === 404) {
          setCurrentPlayer(null);
          setRecentBids([]);
        }
      }
    } catch (err) {
      console.error('Failed to fetch data:', err);
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  // Initial fetch and polling
  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 5000);
    return () => clearInterval(interval);
  }, [fetchData]);

  // Auction control functions
  const handleStart = async () => {
    try {
      await axiosInstance.post(`/v1/events/${eventId}/auction/start`, { confirm: true });
      toast.success('Auction started!');
      onStateChange?.();
      fetchData();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to start auction'));
    }
  };

  const handlePause = async () => {
    try {
      await axiosInstance.post(`/v1/events/${eventId}/auction/pause`, { confirm: true });
      toast.success('Auction paused');
      onStateChange?.();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to pause'));
    }
  };

  const handleResume = async () => {
    try {
      await axiosInstance.post(`/v1/events/${eventId}/auction/resume`, { confirm: true });
      toast.success('Auction resumed');
      onStateChange?.();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to resume'));
    }
  };

  const handleClose = async () => {
    if (!window.confirm('Close the auction? This will lock all wallets and finalize results.')) return;
    try {
      await axiosInstance.post(`/v1/events/${eventId}/auction/close`, { confirm: true });
      toast.success('Auction closed');
      onStateChange?.();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to close auction'));
    }
  };

  const handleNextPlayer = async () => {
    try {
      const res = await axiosInstance.post(`/v1/events/${eventId}/auction/next-player`);
      if (res.data?.has_next_player === false) {
        toast.info('All players have been auctioned');
      } else {
        toast.success('Moved to next player');
      }
      setBidAmount('');
      setSelectedTeam('');
      fetchData();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to move to next player'));
    }
  };

  const handleStartUnsoldRound = async () => {
    if (!window.confirm('Start unsold players round? All UNSOLD players will be made available for bidding again.')) return;
    try {
      // Revert all unsold players to available
      const poolRes = await axiosInstance.get(`/v1/events/${eventId}/auction/pool?status=UNSOLD`);
      const unsoldPlayers = (poolRes.data || []).filter(p => p.status === 'UNSOLD');
      if (unsoldPlayers.length === 0) {
        toast.info('No unsold players to bring back');
        return;
      }
      let reverted = 0;
      for (const player of unsoldPlayers) {
        try {
          await axiosInstance.post(`/v1/events/${eventId}/auction/players/${player.id}/revert`);
          reverted++;
        } catch {}
      }
      toast.success(`Unsold round started! ${reverted} players are available again`);
      fetchData();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to start unsold round'));
    }
  };

  const handlePlaceBid = async () => {
    if (!selectedTeam || !bidAmount) return;
    try {
      setPlacing(true);
      await axiosInstance.post(`/v1/events/${eventId}/auction/bids`, {
        player_pool_id: currentPlayer.id,
        team_id: selectedTeam,
        bid_amount_byc: parseFloat(bidAmount)
      });
      toast.success('Bid recorded');
      setBidAmount('');
      fetchData();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to place bid'));
    } finally {
      setPlacing(false);
    }
  };

  const handleSold = async () => {
    if (!currentPlayer) return;
    const lastBid = recentBids[0];
    if (!lastBid) {
      toast.error('No bids placed yet');
      return;
    }
    try {
      await axiosInstance.post(`/v1/events/${eventId}/auction/players/${currentPlayer.id}/sold`, {
        bid_id: lastBid.id
      });
      toast.success(`${currentPlayer.player_name} SOLD to ${lastBid.team_name}!`);
      fetchData();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to mark as sold'));
    }
  };

  const handleUnsold = async () => {
    if (!currentPlayer) return;
    try {
      await axiosInstance.post(`/v1/events/${eventId}/auction/players/${currentPlayer.id}/unsold`);
      toast.success(`${currentPlayer.player_name} marked UNSOLD`);
      fetchData();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to mark as unsold'));
    }
  };

  // Get next valid bid suggestion
  const getNextBid = () => {
    if (!selectedTeam) return null;
    const team = teams.find(t => t.id === selectedTeam);
    if (!team) return null;
    
    const lastBid = recentBids[0]?.bid_amount_byc || currentPlayer?.base_price_byc || 0;
    const increment = config?.bid_increment_type === 'FIXED' 
      ? config.bid_increment_value 
      : 50000; // Default for SLAB
    
    return lastBid + increment;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#3AAFA9]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="live-auction-console">
      {/* Status & Controls */}
      <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <span className={`px-3 py-1.5 rounded-full text-sm font-bold ${AUCTION_STATUS_COLORS[config?.auction_status] || 'bg-gray-100'}`}>
              {config?.auction_status}
            </span>
            {isPaused && (
              <span className="px-3 py-1.5 rounded-full text-sm font-bold bg-amber-100 text-amber-600">
                <i className="ph-bold ph-pause mr-1"></i> PAUSED
              </span>
            )}
          </div>
          <div className="flex gap-2 flex-wrap">
            {config?.auction_status === 'CONFIGURED' && (
              <div className="flex flex-col items-end gap-1">
                <button
                  onClick={handleStart}
                  disabled={!auctionPhaseStarted}
                  title={!auctionPhaseStarted ? `Auction phase starts ${phaseStartLabel}` : 'Start the auction'}
                  className="px-4 py-2 bg-green-500 text-white rounded-lg font-bold text-sm hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="start-auction-btn"
                >
                  <i className="ph-bold ph-play mr-2"></i>Start Auction
                </button>
                {!auctionPhaseStarted && phaseStartLabel && (
                  <p className="text-xs text-amber-600 flex items-center gap-1">
                    <i className="ph-bold ph-clock"></i>
                    Available from {phaseStartLabel}
                  </p>
                )}
              </div>
            )}
            {isLive && !isPaused && (
              <button
                onClick={handlePause}
                className="px-4 py-2 bg-amber-500 text-white rounded-lg font-bold text-sm hover:bg-amber-600"
                data-testid="pause-auction-btn"
              >
                <i className="ph-bold ph-pause mr-2"></i>Pause
              </button>
            )}
            {isLive && isPaused && (
              <button
                onClick={handleResume}
                className="px-4 py-2 bg-green-500 text-white rounded-lg font-bold text-sm hover:bg-green-600"
                data-testid="resume-auction-btn"
              >
                <i className="ph-bold ph-play mr-2"></i>Resume
              </button>
            )}
            {isLive && (
              <button
                onClick={handleClose}
                className="px-4 py-2 bg-red-500 text-white rounded-lg font-bold text-sm hover:bg-red-600"
                data-testid="close-auction-btn"
              >
                <i className="ph-bold ph-x mr-2"></i>Close
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Live Stats Bar */}
      {liveStats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="bg-blue-50 rounded-xl p-3 text-center">
            <p className="text-2xl font-extrabold text-blue-700">{liveStats.players_sold || 0}</p>
            <p className="text-xs font-medium text-blue-600">Sold</p>
          </div>
          <div className="bg-green-50 rounded-xl p-3 text-center">
            <p className="text-2xl font-extrabold text-green-700">{liveStats.players_available || 0}</p>
            <p className="text-xs font-medium text-green-600">Available</p>
          </div>
          <div className="bg-purple-50 rounded-xl p-3 text-center">
            <p className="text-2xl font-extrabold text-purple-700">₿ {formatBYCCompact(liveStats.total_byc_spent || 0)}</p>
            <p className="text-xs font-medium text-purple-600">Total Spent</p>
          </div>
          <div className="bg-amber-50 rounded-xl p-3 text-center">
            <p className="text-2xl font-extrabold text-amber-700">₿ {formatBYCCompact(liveStats.highest_sale_price || 0)}</p>
            <p className="text-xs font-medium text-amber-600">Highest Sale</p>
          </div>
        </div>
      )}

      {isLive && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Current Player Card - Large */}
          <div className="lg:col-span-2">
            {currentPlayer ? (
              <div className="bg-gradient-to-br from-[#2B3A67] to-[#1e2a4a] rounded-2xl text-white overflow-hidden" data-testid="current-player-card">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-3 bg-black/20">
                  <span className="px-3 py-1 bg-green-500 rounded-full text-xs font-bold animate-pulse">
                    NOW BIDDING
                  </span>
                  <span className="text-sm opacity-70 font-mono">#{currentPlayer.pool_order}</span>
                </div>
                
                {/* Player Info Section */}
                <div className="p-6 relative">
                  {/* Premium SOLD Stamp - positioned on top right of info section */}
                  {currentPlayer.status === 'SOLD' && (
                    <div className="absolute top-4 right-4 z-10 transform rotate-6">
                      <div className="relative">
                        {/* Outer glow */}
                        <div className="absolute inset-0 bg-emerald-500/30 blur-md rounded-lg"></div>
                        {/* Stamp body */}
                        <div className="relative border-2 border-emerald-400 rounded-lg px-4 py-2 bg-gradient-to-br from-emerald-600 to-emerald-800 shadow-lg">
                          <div className="flex items-center gap-2">
                            <i className="ph-fill ph-seal-check text-emerald-200 text-xl"></i>
                            <span className="text-emerald-100 text-lg font-black tracking-wide">SOLD</span>
                          </div>
                          <p className="text-emerald-200 text-xs font-bold text-center mt-0.5 truncate max-w-[120px]">
                            {currentPlayer.sold_to_team_name || 'Team'}
                          </p>
                          {currentPlayer.sold_price_byc > 0 && (
                            <p className="text-emerald-300 text-[10px] text-center font-mono">
                              ₿ {formatBYC(currentPlayer.sold_price_byc)}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Premium UNSOLD Stamp */}
                  {currentPlayer.status === 'UNSOLD' && (
                    <div className="absolute top-4 right-4 z-10 transform -rotate-6">
                      <div className="relative">
                        {/* Outer glow */}
                        <div className="absolute inset-0 bg-rose-500/30 blur-md rounded-lg"></div>
                        {/* Stamp body */}
                        <div className="relative border-2 border-rose-400 rounded-lg px-4 py-2 bg-gradient-to-br from-rose-600 to-rose-800 shadow-lg">
                          <div className="flex items-center gap-2">
                            <i className="ph-fill ph-x-circle text-rose-200 text-xl"></i>
                            <span className="text-rose-100 text-lg font-black tracking-wide">UNSOLD</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  <div className="flex gap-5">
                    {/* Photo */}
                    <div 
                      className={`w-28 h-28 rounded-xl bg-white/10 flex-shrink-0 flex items-center justify-center overflow-hidden ${currentPlayer.player_photo_url ? 'cursor-pointer hover:ring-2 hover:ring-white/50 transition-all' : ''}`}
                      onClick={() => currentPlayer.player_photo_url && setShowPhotoOverlay(true)}
                      title={currentPlayer.player_photo_url ? 'Click to enlarge' : ''}
                    >
                      {currentPlayer.player_photo_url ? (
                        <img src={currentPlayer.player_photo_url} alt={currentPlayer.player_name} className="w-full h-full object-cover" />
                      ) : (
                        <span className="text-4xl font-extrabold">{currentPlayer.player_name?.[0] || '?'}</span>
                      )}
                    </div>
                    
                    {/* Name & Basic Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <h2 className="text-2xl font-extrabold truncate">{currentPlayer.player_name || 'Unknown Player'}</h2>
                        </div>
                        {currentPlayer.player_photo_url && (
                          <button
                            onClick={() => setShowPhotoOverlay(true)}
                            className="p-2 bg-white/10 rounded-lg hover:bg-white/20 transition-colors flex-shrink-0"
                            title="View full photo"
                          >
                            <i className="ph-bold ph-arrows-out text-xl"></i>
                          </button>
                        )}
                      </div>
                      
                      {/* Tags */}
                      <div className="flex flex-wrap gap-2 mt-3">
                        {currentPlayer.player_age && (
                          <span className="px-2 py-1 bg-white/20 rounded text-xs font-bold">Age: {currentPlayer.player_age}</span>
                        )}
                        {currentPlayer.is_icon && (
                          <span className="px-2 py-1 bg-purple-500 rounded text-xs font-bold">ICON</span>
                        )}
                        {currentPlayer.player_category && (
                          <span className="px-2 py-1 bg-white/20 rounded text-xs font-bold">{currentPlayer.player_category}</span>
                        )}
                        {currentPlayer.player_category_level && (
                          <span className="px-2 py-1 bg-amber-500/30 rounded text-xs font-bold">Category {currentPlayer.player_category_level}</span>
                        )}
                        {currentPlayer.batting_style && (
                          <span className="px-2 py-1 bg-blue-500/30 rounded text-xs font-bold">Bat: {currentPlayer.batting_style}</span>
                        )}
                        {currentPlayer.bowling_style && (
                          <span className="px-2 py-1 bg-sky-500/30 rounded text-xs font-bold">Bowl: {currentPlayer.bowling_style}</span>
                        )}
                        {currentPlayer.bowling_type && (
                          <span className="px-2 py-1 bg-orange-500/30 rounded text-xs font-bold">{currentPlayer.bowling_type}</span>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {/* Statistics - Tabbed View */}
                  {currentPlayer.stats && (
                    <CareerStatsTabs stats={currentPlayer.stats} playerCategory={currentPlayer.player_category} />
                  )}
                </div>
                
                {/* Pricing Section */}
                <div className="bg-black/20 p-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-white/10 rounded-xl p-4">
                      <p className="text-xs text-white/60 mb-1">Base Price</p>
                      <p className="text-2xl font-extrabold text-yellow-400">₿ {formatBYC(currentPlayer.base_price_byc)}</p>
                    </div>
                    <div className="bg-white/10 rounded-xl p-4">
                      <p className="text-xs text-white/60 mb-1">Current Bid</p>
                      <p className="text-2xl font-extrabold text-green-400">
                        {currentPlayer.current_bid 
                          ? `₿ ${formatBYC(currentPlayer.current_bid.bid_amount_byc)}`
                          : recentBids[0] 
                            ? `₿ ${formatBYC(recentBids[0].bid_amount_byc)}`
                            : '-'
                        }
                      </p>
                      {(currentPlayer.current_bid || recentBids[0]) && (
                        <p className="text-xs text-white/50 mt-1">
                          by {currentPlayer.current_bid?.team_name || recentBids[0]?.team_name}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Bid Input */}
                {!isPaused && (
                  <div className="p-4 bg-black/10 space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <select
                        value={selectedTeam}
                        onChange={(e) => setSelectedTeam(e.target.value)}
                        className="w-full px-3 py-2.5 rounded-xl bg-white/10 border border-white/20 text-white focus:border-white/40 outline-none"
                        data-testid="bid-team-select"
                      >
                        <option value="">Select Team</option>
                        {teams.filter(t => (t.wallet_balance_byc || 0) > 0).map(team => (
                          <option key={team.id} value={team.id} className="text-gray-800">
                            {team.team_name} (₿{formatBYC(team.wallet_balance_byc || 0)})
                          </option>
                        ))}
                      </select>
                      <input
                        type="number"
                        value={bidAmount}
                        onChange={(e) => setBidAmount(e.target.value)}
                        placeholder={`Next: ₿${formatBYC(getNextBid() || 0)}`}
                        className="w-full px-3 py-2.5 rounded-xl bg-white/10 border border-white/20 text-white placeholder-white/50 focus:border-white/40 outline-none"
                        data-testid="bid-amount-input"
                      />
                    </div>
                    <button
                      onClick={handlePlaceBid}
                      disabled={placing || !selectedTeam || !bidAmount}
                      className="w-full py-3 bg-[#F4D341] text-[#2B3A67] rounded-xl font-extrabold hover:bg-[#e6c73a] disabled:opacity-50"
                      data-testid="place-bid-btn"
                    >
                      {placing ? 'Placing...' : 'Record Bid'}
                    </button>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex gap-3 p-4 bg-black/30">
                  {/* Show Sold/Unsold buttons only for AVAILABLE players */}
                  {currentPlayer.status === 'AVAILABLE' && (
                    <>
                      <button
                        onClick={handleSold}
                        disabled={recentBids.length === 0}
                        className="flex-1 py-3 bg-green-500 text-white rounded-xl font-extrabold hover:bg-green-600 disabled:opacity-50"
                        data-testid="sold-btn"
                      >
                        <i className="ph-bold ph-check mr-2"></i>SOLD
                      </button>
                      <button
                        onClick={handleUnsold}
                        className="flex-1 py-3 bg-red-500 text-white rounded-xl font-extrabold hover:bg-red-600"
                        data-testid="unsold-btn"
                      >
                        <i className="ph-bold ph-x mr-2"></i>UNSOLD
                      </button>
                    </>
                  )}
                  
                  {/* Show Revert button for Admin when player is SOLD/UNSOLD */}
                  {(currentPlayer.status === 'SOLD' || currentPlayer.status === 'UNSOLD') && isAdmin && (
                    <button
                      onClick={handleRevertStatus}
                      className="flex-1 py-3 bg-amber-500 text-white rounded-xl font-extrabold hover:bg-amber-600"
                      data-testid="revert-btn"
                    >
                      <i className="ph-bold ph-arrow-counter-clockwise mr-2"></i>REVERT
                    </button>
                  )}
                  
                  {/* Next button - disabled when no available players and current is SOLD/UNSOLD */}
                  {(() => {
                    const noMorePlayers = (liveStats?.players_available || 0) === 0 && 
                      (currentPlayer.status === 'SOLD' || currentPlayer.status === 'UNSOLD');
                    return (
                      <button
                        onClick={handleNextPlayer}
                        disabled={noMorePlayers}
                        className={`py-3 px-6 rounded-xl font-bold flex items-center gap-2 ${
                          noMorePlayers 
                            ? 'bg-white/10 text-white/30 cursor-not-allowed' 
                            : 'bg-white/20 text-white hover:bg-white/30'
                        }`}
                        data-testid="next-player-btn"
                      >
                        NEXT<i className="ph-bold ph-skip-forward"></i>
                      </button>
                    );
                  })()}
                </div>
                
                {/* Round Complete: show Unsold Round button when no available players */}
                {(liveStats?.players_available || 0) === 0 && 
                  (currentPlayer.status === 'SOLD' || currentPlayer.status === 'UNSOLD') && (
                  <div className="mt-4 pt-4 border-t border-white/10 flex flex-wrap gap-3 justify-center">
                    <button
                      onClick={handleNextPlayer}
                      className="px-5 py-2 bg-[#2B3A67] text-white rounded-lg font-bold hover:bg-[#1e2a4a] text-sm"
                    >
                      Clear Player
                    </button>
                    {config?.enable_unsold_round && (
                      <button
                        onClick={handleStartUnsoldRound}
                        className="px-5 py-2 bg-amber-500 text-white rounded-lg font-bold hover:bg-amber-600 flex items-center gap-2 text-sm"
                        data-testid="unsold-round-inline-btn"
                      >
                        <i className="ph-bold ph-arrow-counter-clockwise"></i>
                        Unsold Round
                      </button>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div className="bg-white rounded-2xl p-8 border border-gray-100 text-center">
                <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                  <i className="ph-bold ph-user text-2xl text-gray-400"></i>
                </div>
                <p className="text-gray-500 mb-4">No player currently up for auction</p>
                <div className="flex gap-3 justify-center">
                  <button
                    onClick={handleNextPlayer}
                    className="px-6 py-2 bg-[#2B3A67] text-white rounded-lg font-bold hover:bg-[#1e2a4a]"
                  >
                    Start with First Player
                  </button>
                  {config?.enable_unsold_round && (
                    <button
                      onClick={handleStartUnsoldRound}
                      className="px-6 py-2 bg-amber-500 text-white rounded-lg font-bold hover:bg-amber-600 flex items-center gap-2"
                      data-testid="unsold-round-btn"
                    >
                      <i className="ph-bold ph-arrow-counter-clockwise"></i>
                      Unsold Round
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar - Recent Bids & Team Balances */}
          <div className="space-y-4">
            {/* Recent Bids */}
            <div className="bg-white rounded-xl p-4 border border-gray-100">
              <h4 className="font-bold mb-3 flex items-center gap-2">
                <i className="ph-bold ph-clock-counter-clockwise text-[#3AAFA9]"></i>
                Recent Bids
              </h4>
              {recentBids.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-4">No bids yet</p>
              ) : (
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {recentBids.map((bid, idx) => (
                    <div
                      key={bid.id}
                      className={`flex items-center justify-between p-2 rounded-lg ${
                        idx === 0 ? 'bg-green-50 border border-green-200' : 'bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-400">#{bid.bid_sequence}</span>
                        <span className="text-sm font-medium truncate max-w-[100px]">{bid.team_name}</span>
                      </div>
                      <span className="text-sm font-bold">₿ {formatBYC(bid.bid_amount_byc)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Team Balances */}
            <div className="bg-white rounded-xl p-4 border border-gray-100">
              <h4 className="font-bold mb-3 flex items-center gap-2">
                <i className="ph-bold ph-wallet text-[#F4D341]"></i>
                Team Wallets
              </h4>
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {teams.sort((a, b) => (b.wallet_balance_byc || 0) - (a.wallet_balance_byc || 0)).map(team => (
                  <div key={team.id} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 rounded bg-[#2B3A67] flex items-center justify-center">
                        <span className="text-white text-xs font-bold">{team.team_name[0]}</span>
                      </div>
                      <span className="text-sm truncate max-w-[100px]">{team.team_name}</span>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-bold">₿ {formatBYC(team.wallet_balance_byc || 0)}</p>
                      <p className="text-[10px] text-gray-400">{team.squad_count || 0} players</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Not Live State */}
      {!isLive && config?.auction_status !== 'CONFIGURED' && (
        <div className="bg-white rounded-xl p-8 border border-gray-100 text-center">
          <i className="ph-bold ph-gavel text-4xl text-gray-300 mb-4"></i>
          <p className="text-gray-500">
            {config?.auction_status === 'CLOSED' 
              ? 'Auction has ended'
              : 'Configure auction settings before starting'
            }
          </p>
        </div>
      )}

      {/* Photo Overlay */}
      {showPhotoOverlay && currentPlayer?.player_photo_url && (
        <PhotoOverlay
          photoUrl={currentPlayer.player_photo_url}
          playerName={currentPlayer.player_name}
          onClose={() => setShowPhotoOverlay(false)}
        />
      )}
    </div>
  );
}
